import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../context/Auth';
import { truncateString } from '../../../helpers/functions';
import { GET_INSTITUTIONS_ASSETS } from '../../../http/apollo-queries';
import { Checkbox } from '../../form';
import { Loading } from '../../UI';
import { Title } from '../../NewStrategy/Title';
import { PlaidAsset } from '../types';
import { upperCase } from 'lodash';
import { BankAssetFormProps } from './types';

export const BankAssets = ({ formik, itemId }: BankAssetFormProps) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { data: { getInstitutionAssets: bankAssets = [] } = {}, loading } =
    useQuery<{ getInstitutionAssets: PlaidAsset[] }>(GET_INSTITUTIONS_ASSETS, {
      variables: { profileId: user?.profile?.id, itemId },
      fetchPolicy: 'no-cache',
    });

  const { selectedBankAssets } = formik.values;

  const handleSelectedBankAssets = (asset: PlaidAsset) => {
    const newAssets = selectedBankAssets.some(
      ({ id: assetId, mask: assetMask }) =>
        assetId + assetMask === asset.id + asset.mask
    )
      ? selectedBankAssets.filter(
          (el) => el.id + el.mask !== asset.id + asset.mask
        )
      : [...selectedBankAssets, asset];

    formik.setValues({ ...formik.values, selectedBankAssets: newAssets });
  };

  return loading ? (
    <Loading className="h-full m-auto" status={loading} />
  ) : (
    <>
      <div className="flex flex-col h-full overflow-y-auto">
        <div>
          <p className={'text-base font-semibold p-4 border-t border-grey-10'}>
            {t('strategies.addAsset.subText')}
          </p>
        </div>
        <div
          className={`
            grid
            grid-cols-6
            items-center
            w-full
            py-4
            bg-white
            border-t
            border-b
            border-grey-10
          `}
        >
          <span className="uppercase col-span-3 mx-3 font-medium text-xs">
            {t('strategies.asset')}
          </span>
          <span className="uppercase font-medium text-xs">
            {t('strategies.configureAsset.form.placeholder.quantity')}
          </span>
          <span className="uppercase font-medium text-xs">
            {t('strategies.totalValue')}
          </span>
          <span className="uppercase font-medium text-xs">
            {t('strategies.addAsset.bank.BANKACCOUNT')}
          </span>
        </div>
        {bankAssets.map(({ bankName, id, name, price, quantity, mask }, i) => (
          <div
            key={id + mask}
            className={`
            grid
            grid-cols-6
            items-center
            w-full
            py-4
            bg-grey-10
            border-b
            border-grey-30
            last:border-0
          `}
          >
            <Title
              className="col-span-3"
              description={truncateString(bankName, 15)}
              logo={null}
              logoRadius="7"
              title={upperCase(name)}
              prefix={
                <Checkbox
                  size={'small'}
                  className="mx-3"
                  isCheck={selectedBankAssets.some(
                    ({ id: assetId, mask: assetMask }) =>
                      assetId + assetMask === id + mask
                  )}
                  shape="square"
                  value={bankAssets[i]}
                  onClick={handleSelectedBankAssets}
                />
              }
            />
            <span className={'text-sm truncate'}>{quantity}</span>
            <span className={'text-sm truncate'}>
              {(price * quantity).toFixed(2)}
            </span>
            <span className="uppercase text-sm truncate">{`${bankName}_${mask}`}</span>
          </div>
        ))}
      </div>
    </>
  );
};
